import React, { useState, useEffect, useCallback } from 'react';
import {
    Wallet, TrendingUp, Users, Receipt, Plus, Trash2, X, Check, PieChart,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/ControlloGestione.css';

/**
 * ControlloGestione — il cruscotto di chi amministra.
 *
 * Risponde a una domanda sola: con quello che c'e' in banca, si arriva
 * a fine mese? Percio' la prima cosa che si vede e' il confronto fra
 * saldi e costi maturati, non l'utile.
 *
 * Attenzione a non confondere le due grandezze: i saldi sono cassa
 * (quanto c'e' adesso), i costi sono competenza (quanto e' dovuto).
 * Il delta non e' un utile, e' un margine di liquidita'.
 */

const euro = (v) => (v == null ? '—' : Number(v).toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}));

const euroPreciso = (v) => (v == null ? '—' : Number(v).toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR',
}));

const meseCorrente = () => new Date().toISOString().slice(0, 7);

const CATEGORIE = [
    ['finanziamento', 'Finanziamenti'],
    ['affitto', 'Affitti'],
    ['assicurazione', 'Assicurazioni'],
    ['utenze', 'Utenze'],
    ['servizi', 'Servizi'],
    ['tributi', 'Tributi'],
    ['altro', 'Altro'],
];

const nomeCategoria = (c) => (CATEGORIE.find((x) => x[0] === c) || [null, c])[1];


/* ---------- grafici ---------- */



/**
 * Ricavi e liquidita' sullo stesso grafico.
 *
 * PERCHE' INSIEME
 * Erano due riquadri separati, e su due assi diversi - i saldi per giorno
 * di aggiornamento, i ricavi per mese - non si poteva vedere la cosa per
 * cui li si guarda: se la liquidita' sale QUANDO salgono i ricavi, o se
 * sale solo perche' non si e' ancora pagato nessuno. Ora condividono i
 * mesi.
 *
 * I due assi verticali restano diversi, e devono: un saldo di
 * quindicimila euro e un fatturato di ottomila non si misurano sulla
 * stessa scala senza schiacciare uno dei due. Le colonne sono i ricavi,
 * la linea la liquidita', e ognuna ha il suo fondoscala.
 *
 * Un mese senza saldi noti interrompe la linea invece di portarla a zero:
 * «non lo so» e «i conti erano vuoti» sono due cose diverse.
 */
const AndamentoUnito = ({ ricavi, saldi, onScegli, scelto }) => {
    if (!ricavi || !ricavi.length) {
        return <p className="cg-vuoto">Ancora nessun mese fatturato.</p>;
    }

    const L = 720, A = 230, SU = 18, GIU = 34, LATO = 10;
    const perMese = Object.fromEntries((saldi || []).map((x) => [x.mese, x.totale]));

    const maxRic = Math.max(...ricavi.map((m) => Number(m.imponibile) || 0), 1);
    const valoriSaldo = ricavi
        .map((m) => (perMese[m.mese] == null ? null : Number(perMese[m.mese])))
        .filter((v) => v !== null);
    const maxSaldo = Math.max(...valoriSaldo, 1);
    const minSaldo = Math.min(...valoriSaldo, 0);

    const passo = (L - LATO * 2) / ricavi.length;
    const centro = (i) => LATO + passo * i + passo / 2;
    const yRic = (v) => A - GIU - (v / maxRic) * (A - SU - GIU);
    const yScd = (v) => A - GIU - ((v - minSaldo) / (maxSaldo - minSaldo || 1)) * (A - SU - GIU);

    /* Spezzoni continui: la linea si interrompe dove il saldo non si sa. */
    const spezzoni = [];
    let corrente = [];
    ricavi.forEach((m, i) => {
        const v = perMese[m.mese];
        if (v == null) { if (corrente.length) spezzoni.push(corrente); corrente = []; return; }
        corrente.push(`${centro(i)},${yScd(Number(v))}`);
    });
    if (corrente.length) spezzoni.push(corrente);

    const etichettaMese = (m) =>
        new Date(m + '-01').toLocaleDateString('it-IT', { month: 'short' });

    return (
        <>
            <svg viewBox={`0 0 ${L} ${A}`} className="cg-grafico-unito">
                {ricavi.map((m, i) => {
                    const v = Number(m.imponibile) || 0;
                    const y = yRic(v);
                    return (
                        <g key={m.mese} className={`cg-u-mese ${scelto === m.mese ? 'scelto' : ''}`}
                           onClick={() => onScegli(scelto === m.mese ? null : m.mese)}>
                            <rect x={centro(i) - passo * 0.32} y={y}
                                  width={passo * 0.64} height={Math.max(A - GIU - y, 0)}
                                  className="cg-u-colonna" />
                            <text x={centro(i)} y={A - 18} textAnchor="middle" className="cg-u-mese-testo">
                                {etichettaMese(m.mese)}
                            </text>
                            <title>
                                {`${m.mese} — ricavi ${euro(v)}`}
                                {perMese[m.mese] != null ? ` · liquidita' ${euro(perMese[m.mese])}` : ''}
                            </title>
                        </g>
                    );
                })}

                {spezzoni.map((punti, i) => (
                    <polyline key={i} points={punti.join(' ')} className="cg-u-linea" />
                ))}
                {ricavi.map((m, i) => (perMese[m.mese] == null ? null : (
                    <circle key={m.mese} cx={centro(i)} cy={yScd(Number(perMese[m.mese]))}
                            r="3" className="cg-u-punto" />
                )))}
            </svg>
            <p className="cg-legenda">
                <span className="cg-legenda-colonna" /> ricavi del mese
                <span className="cg-legenda-linea" /> liquidita&apos; a fine mese
            </p>
        </>
    );
};



/**
 * Ricavi giorno per giorno. I giorni senza fatture restano a zero
 * invece di sparire: un mese che salta le domeniche sembrerebbe piu'
 * corto di quello che e'.
 */
const DettaglioGiorni = ({ mese, onChiudi }) => {
    const [dati, setDati] = useState(null);
    const [caricamento, setCaricamento] = useState(true);

    useEffect(() => {
        let attivo = true;
        setCaricamento(true);
        api.get('/gestione/giornaliero', { params: { mese } })
            .then((r) => { if (attivo) setDati(r.data.data); })
            .catch(() => { if (attivo) setDati(null); })
            .finally(() => { if (attivo) setCaricamento(false); });
        return () => { attivo = false; };
    }, [mese]);

    if (caricamento) return <p className="cg-vuoto">Caricamento…</p>;
    if (!dati?.giorni?.length) return <p className="cg-vuoto">Nessun dato per questo mese.</p>;

    const max = Math.max(...dati.giorni.map((g) => Number(g.imponibile)), 1);
    const nomeMese = new Date(mese + '-01').toLocaleDateString('it-IT',
        { month: 'long', year: 'numeric' });

    return (
        <div className="cg-giorni">
            <div className="cg-giorni-testa">
                <b>{nomeMese}</b>
                <span>{euroPreciso(dati.totale)} in totale</span>
                <button onClick={onChiudi}><X size={15} /></button>
            </div>

            {/* LA CURVA DEL PROGRESSIVO.
                Il backend lo calcola riga per riga, con un commento che
                spiega perche' non si calcola nel grafico - e poi finiva
                solo nel testo che compare passandoci sopra. Un progressivo
                si calcola per vederlo salire: se il mese e' partito piano
                e ha recuperato in fondo, lo dice la curva, non un numero
                che si legge un giorno alla volta. */}
            {dati.giorni.length > 1 && (
                <svg viewBox={`0 0 ${dati.giorni.length * 10} 40`}
                     preserveAspectRatio="none" className="cg-progressivo">
                    <polyline
                        points={dati.giorni.map((g, i) =>
                            `${i * 10 + 5},${40 - (Number(g.progressivo) / (dati.totale || 1)) * 36}`
                        ).join(' ')} />
                </svg>
            )}

            <div className="cg-giorni-barre">
                {dati.giorni.map((g) => {
                    const v = Number(g.imponibile);
                    const giorno = new Date(g.giorno);
                    const festivo = giorno.getDay() === 0;
                    return (
                        <div key={g.giorno} className="cg-giorno" title={
                            `${giorno.toLocaleDateString('it-IT')} — ${euroPreciso(v)}`
                            + (g.fatture ? ` (${g.fatture} fatture)` : '')
                            + ` · progressivo ${euroPreciso(g.progressivo)}`
                        }>
                            <div className="cg-giorno-pista">
                                <div
                                    className={`cg-giorno-barra ${festivo ? 'festivo' : ''}`}
                                    style={{ height: `${v / max * 100}%` }}
                                />
                            </div>
                            <span className="cg-giorno-num">{giorno.getDate()}</span>
                            {/* Quante fatture quel giorno: stava solo nel
                                testo al passaggio del mouse, cioe' non
                                esisteva su un tablet. Due giorni possono
                                valere uguale con una fattura o con dodici,
                                e non e' lo stesso lavoro. */}
                            <span className="cg-giorno-fatture">
                                {g.fatture ? g.fatture : ''}
                            </span>
                        </div>
                    );
                })}
            </div>
            <p className="cg-nota">
                La linea in alto è il progressivo del mese. Il numero sotto ogni
                colonna è quante fatture sono state emesse quel giorno.
            </p>
        </div>
    );
};

const ControlloGestione = () => {
    const navigate = useNavigate();
    const [mese, setMese] = useState(meseCorrente());
    const [dati, setDati] = useState(null);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [scheda, setScheda] = useState(null);   // 'saldo' | 'ricorrente' | 'personale'
    const [elenchi, setElenchi] = useState(
        { ricorrenti: [], personale: [], saldi: [], finanziamenti: [] });
    const [salvato, setSalvato] = useState(false);
    const [andamento, setAndamento] = useState({ saldi: [], ricavi: [] });
    const [meseAperto, setMeseAperto] = useState(null);

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const r = await api.get('/gestione/cruscotto', { params: { mese } });
            setDati(r.data.data);
            setErrore(null);
        } catch (e) {
            setErrore(e.response?.data?.message || 'Non riesco a caricare il cruscotto');
        } finally {
            setCaricamento(false);
        }
    }, [mese]);

    const caricaElenchi = useCallback(async () => {
        try {
            const [a, b, c, d, e] = await Promise.all([
                api.get('/gestione/ricorrenti'),
                api.get('/gestione/personale'),
                api.get('/gestione/saldi'),
                api.get('/gestione/finanziamenti'),
                api.get('/gestione/andamento'),
            ]);
            setAndamento(e.data.data || { saldi: [], ricavi: [] });
            setElenchi({
                ricorrenti: a.data.data || [],
                personale: b.data.data || [],
                saldi: c.data.data || [],
                finanziamenti: d.data.data || [],
            });
        } catch (e) { /* i riquadri restano vuoti */ }
    }, []);

    useEffect(() => { carica(); }, [carica]);
    useEffect(() => { caricaElenchi(); }, [caricaElenchi]);

    const conferma = () => {
        setSalvato(true);
        setTimeout(() => setSalvato(false), 1500);
        carica();
        caricaElenchi();
    };

    const elimina = async (tipo, id) => {
        if (!window.confirm('Eliminare questa voce?')) return;
        try {
            await api.delete(`/gestione/${tipo}/${id}`);
            conferma();
        } catch (e) {
            setErrore(e.response?.data?.message || 'Errore nella cancellazione');
        }
    };

    if (caricamento && !dati) {
        return <div className="cg-wrap"><p className="cg-stato">Caricamento…</p></div>;
    }

    const l = dati?.liquidita;
    const c = dati?.costi;
    const r = dati?.ricavi;
    const maxSpettanza = Math.max(...(dati?.specialisti || []).map((x) => Number(x.spettanza)), 1);

    return (
        <div className="cg-wrap">
            <header className="cg-testata">
                <div>
                    <h1>Controllo di gestione</h1>
                    <p>Saldi e costi maturati a confronto. Non è un utile: è quanto resta.</p>
                </div>
                <div className="cg-azioni">
                    {salvato && <span className="cg-salvato"><Check size={15} /> salvato</span>}
                    {/* Il posto dove uno cerca «quanto rende la ginecologia» è
                        questa schermata, non il menu di Amministrazione: chi è
                        già qui a guardare i numeri non torna indietro per
                        cercarne altri. Il collegamento sta in tutti e due i
                        posti, senza togliere niente. */}
                    <button type="button" className="cg-vaia"
                        onClick={() => navigate('/analisi')}>
                        <PieChart size={15} /> Analisi dei dati
                    </button>
                    <input type="month" value={mese} onChange={(e) => setMese(e.target.value)} />
                </div>
            </header>

            {errore && <div className="cg-errore">{errore}</div>}

            {/* --------- il numero che conta --------- */}
            <section className="cg-liquidita">
                <div className="cg-blocco">
                    <span className="cg-etichetta">In banca e in cassa</span>
                    <b className="cg-cifra">{euro(l?.totale_saldi)}</b>
                    <div className="cg-dettaglio">
                        {(l?.saldi || []).map((s) => (
                            <span key={s.conto}>{s.conto} {euro(s.saldo)}</span>
                        ))}
                        {!l?.saldi?.length && <span className="cg-vuoto">nessun saldo inserito</span>}
                    </div>
                </div>

                <div className="cg-segno">−</div>

                <div className="cg-blocco">
                    <span className="cg-etichetta">Da pagare questo mese</span>
                    <b className="cg-cifra">{euro(l?.totale_costi)}</b>
                    <div className="cg-dettaglio">
                        <span>ricorrenti {euro(c?.totale_ricorrenti)}</span>
                        <span>personale {euro(c?.personale)}</span>
                        <span>specialisti {euro(c?.specialisti)}</span>
                    </div>
                </div>

                <div className="cg-segno">=</div>

                <div className={`cg-blocco cg-risultato ${l?.delta >= 0 ? 'positivo' : 'negativo'}`}>
                    <span className="cg-etichetta">Resta</span>
                    <b className="cg-cifra">{euro(l?.delta)}</b>
                    <div className="cg-dettaglio">
                        <span>{l?.delta >= 0 ? 'si arriva a fine mese' : 'mancano soldi'}</span>
                    </div>
                </div>
            </section>

            {/* --------- ricavi --------- */}
            <section className="cg-riquadri">
                <div className="cg-riquadro">
                    <h2><TrendingUp size={17} strokeWidth={1.7} />Ricavi del mese</h2>
                    <div className="cg-numeroni">
                        <div>
                            <b>{euroPreciso(r?.totale)}</b>
                            <span>maturati</span>
                        </div>
                        <div>
                            <b>{euroPreciso(r?.incassato)}</b>
                            <span>incassati</span>
                        </div>
                        <div>
                            <b>{euroPreciso(r?.proiezione_fine_mese)}</b>
                            <span>proiezione a fine mese</span>
                        </div>
                    </div>
                    <p className="cg-nota">
                        Su {dati?.periodo?.giorniTrascorsi} giorni di {dati?.periodo?.giorniTotali}.
                        La proiezione è lineare: vale come ordine di grandezza.
                    </p>

                    {/* Ricavi per macro area: la domanda vera del controllo
                        di gestione. La barra e' proporzionale al maggiore,
                        cosi' si legge il peso relativo senza leggere i numeri. */}
                    {r?.per_area?.length > 0 && (
                        <div className="cg-aree">
                            {/* BENE O SERVIZIO.
                                Era calcolato a ogni caricamento e non
                                compariva da nessuna parte: una query a
                                vuoto. Si mostra, perche' risponde a una
                                domanda che «per area» non tocca - quanto
                                di quello che si fattura e' materiale. E'
                                la stessa distinzione che il Sistema TS
                                pretende sulle righe. */}
                            {r.per_tipologia?.length > 0 && (
                                <div className="cg-tipologie">
                                    {r.per_tipologia.map((t) => (
                                        <div key={t.tipologia || 'altro'} className="cg-tipologia">
                                            <span>{t.tipologia === 'bene' ? 'Beni' : t.tipologia === 'pacchetto' ? 'Pacchetti' : 'Servizi'}</span>
                                            <b>{euroPreciso(t.imponibile)}</b>
                                            {Number(t.costo_prodotto) > 0 && (
                                                <em>di cui {euroPreciso(t.costo_prodotto)} di materiale</em>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <h3>Per area</h3>
                            <ul className="cg-elenco cg-aree-elenco">
                                {r.per_area.map((x) => {
                                    const max = Math.max(
                                        ...r.per_area.map((y) => Number(y.imponibile) || 0), 1);
                                    const quota = (Number(x.imponibile) || 0) / max * 100;
                                    return (
                                        <li key={x.area}>
                                            <div className="cg-area-riga">
                                                <span>{x.area}</span>
                                                <b>{euroPreciso(x.imponibile)}</b>
                                            </div>
                                            <div className="cg-area-barra">
                                                <div style={{ width: `${quota}%` }} />
                                            </div>
                                            <em className="cg-area-margine">
                                                margine {euroPreciso(x.margine)}
                                            </em>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="cg-riquadro">
                    <h2><Receipt size={17} strokeWidth={1.7} />Ritenute in scadenza</h2>
                    {!dati?.ritenute?.length ? (
                        <p className="cg-vuoto">Nessuna scadenza registrata.</p>
                    ) : (
                        <ul className="cg-elenco">
                            {dati.ritenute.map((x) => (
                                <li key={x.id}>
                                    <span>{new Date(x.periodo).toLocaleDateString('it-IT',
                                        { month: 'long', year: 'numeric' })}</span>
                                    <b>{euroPreciso(x.importo)}</b>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="cg-nota">Solo consultazione: gli importi arrivano da fuori.</p>
                </div>
            </section>

            {/* --------- specialisti --------- */}
            <section className="cg-riquadro">
                <h2><Users size={17} strokeWidth={1.7} />Compensi degli specialisti</h2>
                {!dati?.specialisti?.length ? (
                    <p className="cg-vuoto">
                        Nessuna spettanza maturata in questo mese.
                    </p>
                ) : (
                    <div className="cg-barre">
                        {dati.specialisti.map((s) => (
                            <div key={s.medico_id} className="cg-barra-riga">
                                <span className="cg-barra-nome">{s.cognome} {s.nome}</span>
                                <div className="cg-barra-pista">
                                    <div
                                        className="cg-barra"
                                        style={{ width: `${Number(s.spettanza) / maxSpettanza * 100}%` }}
                                    />
                                </div>
                                <span className="cg-barra-cifra">
                                    {euroPreciso(s.spettanza)}
                                    {s.ritenuta > 0 && (
                                        <em title="al netto della ritenuta d'acconto">
                                            netto {euroPreciso(s.netto)}
                                        </em>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* --------- voci gestibili --------- */}
            <section className="cg-riquadri">
                <div className="cg-riquadro">
                    <h2>
                        <Wallet size={17} strokeWidth={1.7} />Costi ricorrenti
                        <button className="cg-piu" onClick={() => setScheda({ tipo: 'ricorrenti' })}>
                            <Plus size={15} />
                        </button>
                    </h2>
                    {!elenchi.ricorrenti.length ? (
                        <p className="cg-vuoto">Nessuna voce.</p>
                    ) : (
                        <ul className="cg-elenco">
                            {elenchi.ricorrenti.map((x) => (
                                <li key={x.id} className={x.attivo ? '' : 'spenta'}>
                                    <span>
                                        {x.descrizione}
                                        <em>{nomeCategoria(x.categoria)}
                                            {x.rate_residue != null ? ` · ${x.rate_residue} rate` : ''}</em>
                                    </span>
                                    <b>{euroPreciso(x.importo_mensile)}</b>
                                    <button onClick={() => elimina('ricorrenti', x.id)}>
                                        <Trash2 size={14} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="cg-riquadro">
                    <h2>
                        <Users size={17} strokeWidth={1.7} />Personale
                        <button className="cg-piu" onClick={() => setScheda({ tipo: 'personale' })}>
                            <Plus size={15} />
                        </button>
                    </h2>
                    {!elenchi.personale.length ? (
                        <p className="cg-vuoto">Nessun dipendente.</p>
                    ) : (
                        <ul className="cg-elenco">
                            {elenchi.personale.map((x) => (
                                <li key={x.id} className={x.attivo ? '' : 'spenta'}>
                                    <span>{x.nominativo}<em>{x.ruolo || ''}</em></span>
                                    <b>{euroPreciso(x.importo_mensile)}</b>
                                    <button onClick={() => elimina('personale', x.id)}>
                                        <Trash2 size={14} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="cg-nota">
                        Solo dipendenti: i compensi dei medici maturano sull&apos;eseguito.
                    </p>
                </div>
            </section>

            <section className="cg-riquadro">
                <h2>
                    <Wallet size={17} strokeWidth={1.7} />Saldi dei conti
                    <button className="cg-piu" onClick={() => setScheda({ tipo: 'saldi' })}>
                        <Plus size={15} />
                    </button>
                </h2>
                {!elenchi.saldi.length ? (
                    <p className="cg-vuoto">Nessun saldo inserito.</p>
                ) : (
                    <ul className="cg-elenco cg-elenco--saldi">
                        {elenchi.saldi.slice(0, 12).map((x) => (
                            <li key={x.id}>
                                <span>
                                    {x.conto}
                                    <em>{new Date(x.data_saldo).toLocaleDateString('it-IT')}</em>
                                </span>
                                <b>{euroPreciso(x.saldo)}</b>
                                <button onClick={() => elimina('saldi', x.id)}>
                                    <Trash2 size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <p className="cg-nota">
                    Un valore per conto e per giorno: reinserendo lo stesso giorno si corregge.
                </p>
            </section>

            {/* --------- andamento --------- */}
            <section className="cg-riquadri">
                <div className="cg-riquadro cg-riquadro--largo">
                    <h2><TrendingUp size={17} strokeWidth={1.7} />Ricavi e liquidità</h2>
                    <AndamentoUnito ricavi={andamento.ricavi}
                                    saldi={andamento.saldi_mensili}
                                    onScegli={setMeseAperto} scelto={meseAperto} />
                    {/* Da quando i numeri sono veri lo dice la clinica, non
                        il programma: una fattura di collaudo e una vera hanno
                        la stessa forma. Finché nessuno lo dichiara, il
                        grafico avvisa — meglio un grafico che si dichiara
                        inaffidabile di uno che tace. */}
                    <p className={andamento.dati_reali_dal ? 'cg-nota' : 'cg-nota cg-nota-avviso'}>
                        {andamento.dati_reali_dal ? (
                            <>I numeri sono reali dal <b>
                                {new Date(andamento.dati_reali_dal).toLocaleDateString('it-IT')}
                            </b>. Quello che sta prima è periodo di collaudo.</>
                        ) : (
                            <><b>Nessuna data di inizio dichiarata:</b> questo storico
                            comprende il periodo di collaudo, e una parte dei numeri
                            è di prova. Si dichiara nei parametri della struttura.</>
                        )}
                        {andamento.mesi_con_dati != null && andamento.mesi_con_dati < 3 && (
                            <> Con <b>{andamento.mesi_con_dati} {andamento.mesi_con_dati === 1
                                ? 'mese' : 'mesi'}</b> di dati non c&apos;è ancora una
                            tendenza da leggere: è una fotografia, non un andamento.</>
                        )}
                    </p>
                    <p className="cg-nota">
                        Un mese si apre facendo clic sulla sua colonna. La linea si
                        interrompe dove non c&apos;è nessun saldo registrato: non è zero,
                        è che non si sa.
                    </p>
                    {meseAperto && (
                        <DettaglioGiorni mese={meseAperto}
                                         onChiudi={() => setMeseAperto(null)} />
                    )}
                </div>
            </section>

            {/* --------- finanziamenti --------- */}
            <section className="cg-riquadro">
                <h2>
                    <Wallet size={17} strokeWidth={1.7} />Finanziamenti attesi
                    <button className="cg-piu" onClick={() => setScheda({ tipo: 'finanziamenti' })}>
                        <Plus size={15} />
                    </button>
                </h2>
                {!elenchi.finanziamenti?.length ? (
                    <p className="cg-vuoto">Nessun finanziamento in attesa.</p>
                ) : (
                    <ul className="cg-elenco">
                        {elenchi.finanziamenti.map((x) => (
                            <li key={x.id} className={x.incassato ? 'spenta' : ''}>
                                <span>{x.descrizione}<em>
                                    {x.ente || ''}
                                    {x.data_competenza
                                        ? ` · competenza ${new Date(x.data_competenza).toLocaleDateString('it-IT')}`
                                        : ' · senza data di competenza'}
                                    {/* Quanto resta da rimborsare: rate per rata.
                                        Senza una rata collegata non si sa, e si
                                        dice invece di mostrare zero. */}
                                    {x.rata == null
                                        ? ' · nessuna rata collegata'
                                        : x.residuo == null
                                        ? ` · rata ${euroPreciso(x.rata)}, senza scadenza`
                                        : ` · restano ${x.rate_residue} rate, ${euroPreciso(x.residuo)}`}
                                </em></span>
                                <b>{euroPreciso(x.importo)}</b>
                                <button onClick={() => elimina('finanziamenti', x.id)}>
                                    <Trash2 size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <p className="cg-nota">
                    <b>Non entrano nel fatturato</b>, ed è voluto: il fatturato lo
                    fanno le prestazioni eseguite, e un contributo pubblico sommato
                    lì gonfierebbe i ricavi di una clinica che non ha visitato
                    nessuno in più. La data di competenza dice a quale periodo si
                    riferiscono; l&apos;accredito in banca è un&apos;altra data.
                </p>
            </section>

            {scheda && (
                <SchedaVoce
                    tipo={scheda.tipo}
                                finanziamenti={elenchi.finanziamenti || []}
                    onChiudi={() => setScheda(null)}
                    onSalvato={() => { setScheda(null); conferma(); }}
                    onErrore={setErrore}
                />
            )}
        </div>
    );
};

/* ---------- scheda di inserimento ---------- */

const SchedaVoce = ({ tipo, onChiudi, onSalvato, onErrore, finanziamenti = [] }) => {
    const [v, setV] = useState({
        descrizione: '', categoria: 'altro', importo_mensile: '', rate_residue: '',
        finanziamento_id: '',
        nominativo: '', ruolo: '', conto: '', saldo: '', note: '',
    });
    const [inCorso, setInCorso] = useState(false);

    const titoli = {
        ricorrenti: 'Nuovo costo ricorrente',
        personale: 'Nuovo dipendente',
        saldi: 'Saldo di un conto',
        finanziamenti: 'Finanziamento atteso',
    };

    const salva = async () => {
        try {
            setInCorso(true);
            await api.post(`/gestione/${tipo}`, v);
            onSalvato();
        } catch (e) {
            onErrore(e.response?.data?.message || 'Errore nel salvataggio');
        } finally {
            setInCorso(false);
        }
    };

    return (
        <div className="cg-modale-fondo" onClick={onChiudi}>
            <div className="cg-modale" onClick={(e) => e.stopPropagation()}>
                <header>
                    <h3>{titoli[tipo]}</h3>
                    <button onClick={onChiudi}><X size={18} /></button>
                </header>

                {tipo === 'ricorrenti' && (
                    <>
                        <label className="cg-campo">
                            <span>Descrizione</span>
                            <input value={v.descrizione} placeholder="Mutuo, leasing, RC…"
                                   onChange={(e) => setV({ ...v, descrizione: e.target.value })} />
                        </label>
                        <label className="cg-campo">
                            <span>Categoria</span>
                            <select value={v.categoria}
                                    onChange={(e) => setV({ ...v, categoria: e.target.value })}>
                                {CATEGORIE.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
                            </select>
                        </label>
                        <div className="cg-campi-doppi">
                            <label className="cg-campo">
                                <span>Importo mensile</span>
                                <input type="number" step="10" value={v.importo_mensile}
                                       onChange={(e) => setV({ ...v, importo_mensile: e.target.value })} />
                            </label>
                            <label className="cg-campo">
                                <span>Rate residue</span>
                                <input type="number" value={v.rate_residue} placeholder="vuoto = sempre"
                                       onChange={(e) => setV({ ...v, rate_residue: e.target.value })} />
                            </label>
                        </div>
                        {/* Il collegamento compare solo per le rate: chiedere
                            «quale finanziamento» accanto a una bolletta della
                            luce non aiuta nessuno. */}
                        {v.categoria === 'finanziamento' && (
                            <label className="cg-campo">
                                <span>Quale finanziamento</span>
                                <select value={v.finanziamento_id}
                                        onChange={(e) => setV({ ...v, finanziamento_id: e.target.value })}>
                                    <option value="">Nessuno registrato</option>
                                    {finanziamenti.map((f) => (
                                        <option key={f.id} value={f.id}>
                                            {f.descrizione}{f.ente ? ` — ${f.ente}` : ''}
                                        </option>
                                    ))}
                                </select>
                                <small className="cg-aiuto">
                                    Collegandola, il residuo si calcola da sé e la rata
                                    si scala ogni mese.
                                </small>
                            </label>
                        )}
                    </>
                )}

                {tipo === 'personale' && (
                    <>
                        <label className="cg-campo">
                            <span>Nominativo</span>
                            <input value={v.nominativo}
                                   onChange={(e) => setV({ ...v, nominativo: e.target.value })} />
                        </label>
                        <div className="cg-campi-doppi">
                            <label className="cg-campo">
                                <span>Ruolo</span>
                                <input value={v.ruolo} placeholder="segreteria, infermiere…"
                                       onChange={(e) => setV({ ...v, ruolo: e.target.value })} />
                            </label>
                            <label className="cg-campo">
                                <span>Costo mensile</span>
                                <input type="number" step="10" value={v.importo_mensile}
                                       onChange={(e) => setV({ ...v, importo_mensile: e.target.value })} />
                            </label>
                        </div>
                    </>
                )}

                {tipo === 'finanziamenti' && (
                    <>
                        <label className="cg-campo">
                            <span>Descrizione</span>
                            <input value={v.descrizione} placeholder="Conto termico, Legge Sabatini…"
                                   onChange={(e) => setV({ ...v, descrizione: e.target.value })} />
                        </label>
                        <div className="cg-campi-doppi">
                            <label className="cg-campo">
                                <span>Ente</span>
                                <input value={v.ente || ''}
                                       onChange={(e) => setV({ ...v, ente: e.target.value })} />
                            </label>
                            <label className="cg-campo">
                                <span>Importo</span>
                                <input type="number" step="10" value={v.importo || ''}
                                       onChange={(e) => setV({ ...v, importo: e.target.value })} />
                            </label>
                        </div>
                        {/* Competenza, non incasso: un contributo deliberato
                            a settembre e accreditato a febbraio appartiene a
                            settembre. La data dell'accredito si segna quando
                            arriva, ed è un'altra cosa. */}
                        <label className="cg-campo">
                            <span>Data di competenza</span>
                            <input type="date" value={v.data_competenza || ''}
                                   onChange={(e) => setV({ ...v, data_competenza: e.target.value })} />
                        </label>
                        <label className="cg-campo">
                            <span>Note</span>
                            <input value={v.note || ''} placeholder="Delibera, protocollo…"
                                   onChange={(e) => setV({ ...v, note: e.target.value })} />
                        </label>
                    </>
                )}

                {tipo === 'saldi' && (
                    <div className="cg-campi-doppi">
                        <label className="cg-campo">
                            <span>Conto</span>
                            <input value={v.conto} placeholder="BPER, Cassa…"
                                   onChange={(e) => setV({ ...v, conto: e.target.value })} />
                        </label>
                        <label className="cg-campo">
                            <span>Saldo</span>
                            <input type="number" step="10" value={v.saldo}
                                   onChange={(e) => setV({ ...v, saldo: e.target.value })} />
                        </label>
                    </div>
                )}

                <label className="cg-campo">
                    <span>Note</span>
                    <input value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} />
                </label>

                <footer>
                    <button className="cg-btn" onClick={onChiudi}>Annulla</button>
                    <button className="cg-btn primario" onClick={salva} disabled={inCorso}>
                        Salva
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default ControlloGestione;
